// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductCompareFlyOut from "@insite/content-library/Components/ProductCompareFlyOut";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@pace/App/Components/Common/XngZone";
import ProductDetailsReplacementAnchor from "@pace/App/Components/ProductDetails/ProductDetailsReplacementAnchor";
import ProductDetailsSummary from "@pace/App/Components/ProductDetails/ProductDetailsSummary";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    pageContainer?: InjectableCss;
    bottomSection?: InjectableCss;
    columnsContainer?: InjectableCss;
    twoColumnsSection?: InjectableCss;
    imageDescriptionActionsContainer?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    imageContainer?: InjectableCss;
    specificationsContainer?: InjectableCss;
    generalActionsContainer?: InjectableCss;
    priceActionsContainer?: InjectableCss;
    extraElementsContainer?: InjectableCss;
    divider?: InjectableCss;
    infoDescriptionSection?: InjectableCss;
    videosAndReplacementContainer?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    badgesContainer?: InjectableCss;
    stickyWrapper?: InjectableCss;
    stickyPage?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

// XNG-Change Add shell context

type Props = WidgetProps & HasShellContext;

// XNG-Change: change structure to match design, Add sticky header
const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = React.useState(false);
    const replacementContainerRef = React.createRef<HTMLDivElement>();
    const addToCartRef = React.useRef<HTMLDivElement | null>(null);

    const isShellEditingMood = props.shellContext?.isEditing;

    const handleSticky = () => {
        /* we need the height of the header to be excluded as we will overlap this area (add to cart button) */
        const partiallyPdpInfoHeight = (addToCartRef.current?.scrollHeight || 0) + 60;
        if (window.scrollY > partiallyPdpInfoHeight) {
            setStickyVisible(true);
        } else {
            setStickyVisible(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, []);

    const RightSideSection = (
        <StyledWrapper {...styles.rightColumn} data-test-selector="productDetails_rightColumn">
            <StyledWrapper {...styles.priceActionsContainer} ref={addToCartRef}>
                <XngZone contentId={props.id} zoneName="Content4" hint="Print & Price & Actions" />
            </StyledWrapper>
            {/* <ProductDetailsReplacementAnchor reference={replacementContainerRef} /> */}
            <StyledWrapper {...styles.extraElementsContainer}>
                <XngZone contentId={props.id} zoneName="Content7" hint="Extra Elements" />
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.pageContainer}>
            <StyledWrapper {...styles.columnsContainer}>
                <StyledWrapper {...styles.leftColumn} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper {...styles.imageDescriptionActionsContainer}>
                        <StyledWrapper {...styles.twoColumnsSection}>
                            <XngZone contentId={props.id} zoneName="Content0" hint="Image" />
                            <StyledWrapper {...styles.infoDescriptionSection}>
                                <XngZone contentId={props.id} zoneName="Content5" hint="Title And Info zone" />
                                <StyledWrapper {...styles.productNumbersWrapper}>
                                    {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                                        fields: {},
                                    })}
                                    {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                                        fields: {},
                                    })}
                                    {createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                                        fields: {},
                                    })}
                                </StyledWrapper>
                                {createWidgetElement("Product/ProductBadgesCarousel", {
                                    fields: {},
                                })}
                                <StyledWrapper {...styles.divider} />
                                <XngZone contentId={props.id} zoneName="Content6" hint="Description" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <Hidden above="md">
                            {RightSideSection}
                        </Hidden>
                    </StyledWrapper>
                    <StyledWrapper {...styles.specificationsContainer}>
                        <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Specifications" />
                    </StyledWrapper>
                    {/* XNG-Changes: PAC-1377 */}
                    <StyledWrapper {...styles.videosAndReplacementContainer}  id="productDetailsReplacementAnchor" ref={replacementContainerRef}>
                        <XngZone contentId={props.id} zoneName="Content2" hint="Replacement & Videos" />
                    </StyledWrapper>
                </StyledWrapper>
                <Hidden below="lg">
                    {RightSideSection}
                </Hidden>
            </StyledWrapper>
            <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                <XngZone contentId={props.id} zoneName="Content10" hint="Zone For Product carousel" />
            </StyledWrapper>
            <StyledWrapper
                className={`${stickyVisible ? "stickyVisible" : "stickyHidden"} ${
                    isShellEditingMood ? "editingMode" : ""
                }`}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyWrapper}
            >
                <Page background="common.white" {...styles.stickyPage}>
                    <ProductDetailsSummary />
                </Page>
            </StyledWrapper>
            <ProductCompareFlyOut />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
